import { Component, computed, inject, input, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { AssistantToolLine } from '@core/models/assistant/assistant.model';
import { ICONS } from '@core/services/icons/icons.service';

export function formatDuration(ms: number): string {
  // Each unit is rounded before it is compared or split, so no value reads as 1000 ms, 60.0 s or 1 m 60 s.
  if (Math.round(ms) < 1000) return `${Math.round(ms)} ms`;
  const tenths = Math.round(ms / 100);
  if (tenths < 600) return `${(tenths / 10).toFixed(1)} s`;
  const seconds = Math.round(ms / 1000);
  return `${Math.floor(seconds / 60)} m ${seconds % 60} s`;
}

// Arguments longer than this are cut to one line until the reader asks for more.
const SHORT_ARGUMENTS = 60;

@Component({
  selector: 'app-assistant-tool-line',
  imports: [TranslatePipe],
  templateUrl: './assistant-tool-line.component.html',
  styles: ``
})
export class AssistantToolLineComponent {
  private readonly translate = inject(TranslateService);
  readonly tool = input.required<AssistantToolLine>();
  /** Shows the step's duration and arguments, as inside an expanded tool group. */
  readonly detail = input(false);
  protected readonly showAll = signal(false);
  protected readonly args = computed(() => JSON.stringify(this.tool().arguments));
  protected readonly long = computed(() => this.args().length > SHORT_ARGUMENTS);
  protected readonly duration = computed(() => {
    const ms = this.tool().durationMs;
    return ms === undefined ? null : formatDuration(ms);
  });

  protected tag(): string | null {
    const tool = this.tool();
    if (tool.access === 'read') return null;
    if (tool.status === 'cancelled') return 'assistant.tool_tags.stopped_change';
    return tool.approval === 'user'
      ? 'assistant.tool_tags.approved'
      : 'assistant.tool_tags.auto';
  }

  protected icon(): string {
    const tool = this.tool();
    if (tool.status === 'ok') {
      if (tool.access === 'read') return ICONS.check;
      return tool.access === 'destructive' ? ICONS.warning : ICONS.pencil;
    }
    if (tool.status === 'failed') return ICONS.warning;
    if (tool.status === 'cancelled') return ICONS.x;
    return ICONS.arrowsClockwise + ' animate-spin';
  }

  protected label(): string {
    const tool = this.tool();
    const key = `assistant.tools.${tool.name}`;
    const label = this.translate.instant(key, tool.arguments);
    return label === key
      ? this.translate.instant('assistant.tools.generic', { name: tool.name })
      : label;
  }
}
